import { useEffect, useMemo, useState } from 'react';
import { Account } from '../models/accounts';

import { Option } from 'common/components/Inputs/Autocomplete/utils/models';
import { useAccountsByIds } from './useAccountsByIds';
import { useInfinityAccountsQuery } from './useInfinityAccountsQuery';
import { OPTION_TYPE } from 'src/common/components/Inputs/Autocomplete/utils/schemas';
import { useSaveOnMountArrayState } from 'common/hooks/useSaveOnMountArrayState.ts';

type UseAccountsAutocompleteProps = {
  initialIds: (string | null)[];
  search: string;
  hints: string[];
  onSelectedChange: (options: Option[]) => void;
};

const blankOptionAccountName = 'blank';

type AccountRefWithBlank = Account | { accountRef: null; accountName: string };

const mapAccountToOption = (accounts: AccountRefWithBlank[]) => {
  return accounts.map((account) => {
    if (account.accountRef === null) {
      return { id: null, name: blankOptionAccountName, type: OPTION_TYPE.BLANK };
    }
    return {
      id: account.accountRef.toString(),
      name: account.accountName,
    };
  });
};

export const useAccountsAutocomplete = ({
  search,
  initialIds,
  hints,
  onSelectedChange,
}: UseAccountsAutocompleteProps) => {
  const displayHints = !search;
  const initialSelectedIds = useSaveOnMountArrayState(initialIds);

  const {
    accounts: accountsData,
    isFetched,
    ...accountsQuery
  } = useInfinityAccountsQuery(search, {
    minTime: 250,
  });

  const {
    selectedAccounts: initialSelectedAccounts,
    isPending: isIntialFetching,
    isFetched: isIntialFetched,
  } = useAccountsByIds(initialSelectedIds.filter((id) => id !== null));

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const shouldResetOptions = initialIds.length === 0 && selectedOptions.length !== 0;

  useEffect(() => {
    if (isIntialFetched) {
      setSelectedOptions(mapAccountToOption(initialSelectedAccounts));
    }
  }, [initialSelectedAccounts, isIntialFetched]);

  const updateSelectedOptions = (options: Option[]) => {
    setSelectedOptions(options);
    onSelectedChange(options);
  };

  const { selectedAccounts: hintedAccounts, isFetched: isHintFetched } = useAccountsByIds(hints);
  const hintOptions = useMemo(
    () => (displayHints ? mapAccountToOption(hintedAccounts) : []),
    [hintedAccounts, displayHints],
  );

  const options = useMemo(() => {
    const isBlankSearched = !!search && blankOptionAccountName.includes(search.trim());
    return mapAccountToOption([
      ...(isBlankSearched ? [{ accountRef: null, accountName: blankOptionAccountName }] : []),
      ...accountsData,
    ]);
  }, [accountsData, search]);

  useEffect(() => {
    if (shouldResetOptions) {
      updateSelectedOptions([]);
    }
  }, [shouldResetOptions]);

  const optionsToReturn = useMemo(() => {
    return initialIds.includes(null) && !selectedOptions.some((option) => option.id === null)
      ? [{ id: null, name: blankOptionAccountName, type: OPTION_TYPE.BLANK }, ...selectedOptions]
      : selectedOptions;
  }, [initialIds, selectedOptions]);

  return {
    selectedOptions: optionsToReturn,
    isSelectedOptionFetching: isIntialFetching,
    hintOptions: hintOptions,
    updateSelectedOptions,
    options: options,
    isFetched: isFetched && isHintFetched,
    ...accountsQuery,
  };
};
